import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { PieChart, Pie, Cell, Tooltip, Sector } from "recharts";
import { STATUS_COLORS, STATUS_LABELS, CHART_TITLE, TOOLTIP_BORDER } from "./constants";
import { computeTotal } from "./utils";

const FALLBACK_COLORS = ["#64748b", "#475569", "#334155", "#1e293b"];
const getColor = (status, index) =>
  STATUS_COLORS[status] || STATUS_COLORS[status?.toUpperCase()] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
const getLabel = status =>
  STATUS_LABELS[status] ||
  STATUS_LABELS[status?.toUpperCase()] ||
  (status || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());

const renderActiveShape = props => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius - 2}
      outerRadius={outerRadius + 4}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

export default function StatusDonutChart({ data, title: titleOverride }) {
  const total = computeTotal(data);
  const [hoveredStatus, setHoveredStatus] = useState(null);

  const activeIndex = hoveredStatus !== null ? data.findIndex(d => d.status === hoveredStatus) : -1;

  const onPieEnter = useCallback(
    (_, index) => {
      setHoveredStatus(data[index]?.status ?? null);
    },
    [data]
  );
  const onPieLeave = useCallback(() => setHoveredStatus(null), []);

  return (
    <div className="status-donut">
      <div className="status-donut__header">
        <span className="status-donut__title">{titleOverride || CHART_TITLE}</span>
        <span className="status-donut__badge">{total} total</span>
      </div>
      <div className="status-donut__body">
        <div className="status-donut__chart">
          <PieChart width={180} height={180}>
            <Pie
              data={data}
              cx={88}
              cy={88}
              innerRadius={50}
              outerRadius={78}
              dataKey="count"
              nameKey="status"
              paddingAngle={3}
              strokeWidth={0}
              activeIndex={activeIndex >= 0 ? activeIndex : undefined}
              activeShape={renderActiveShape}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
            >
              {data.map((entry, i) => (
                <Cell
                  key={entry.status}
                  fill={getColor(entry.status, i)}
                  opacity={hoveredStatus && hoveredStatus !== entry.status ? 0.35 : 1}
                  style={{ transition: "opacity 0.2s" }}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [value, getLabel(name)]}
              contentStyle={{ borderRadius: 8, fontSize: 12, border: TOOLTIP_BORDER }}
            />
            <text
              x={90}
              y={84}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: "1.4rem", fontWeight: 800, fill: "#1e293b" }}
            >
              {total}
            </text>
            <text
              x={90}
              y={102}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: "0.62rem", fontWeight: 500, fill: "#94a3b8" }}
            >
              chargers
            </text>
          </PieChart>
        </div>
        {/* <div className="status-donut__legend">
          {data.map(item => (
            <div
              className={`status-donut__legend-item ${hoveredStatus === item.status ? "status-donut__legend-item--active" : ""}`}
              key={item.status}
              onMouseEnter={() => setHoveredStatus(item.status)}
              onMouseLeave={() => setHoveredStatus(null)}
            >
              <span className="status-donut__legend-left">
                <span className="status-donut__legend-dot" style={{ background: STATUS_COLORS[item.status] }} />
                <span className="status-donut__legend-label">{STATUS_LABELS[item.status]}</span>
              </span>
              <span className="status-donut__legend-count">{item.count}</span>
            </div>
          ))}
        </div> */}
      </div>
      <div className="chart-data-table">
        <table className="chart-data-table__table">
          <thead>
            <tr>
              <th className="chart-data-table__th">Status</th>
              <th className="chart-data-table__th chart-data-table__th--center">Chargers</th>
              <th className="chart-data-table__th chart-data-table__th--right">%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, i) => {
              const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0.0";
              return (
                <tr
                  key={item.status}
                  className="chart-data-table__row"
                  onMouseEnter={() => setHoveredStatus(item.status)}
                  onMouseLeave={() => setHoveredStatus(null)}
                >
                  <td className="chart-data-table__td">
                    <span className="chart-data-table__dot" style={{ background: getColor(item.status, i) }} />
                    {getLabel(item.status)}
                  </td>
                  <td className="chart-data-table__td chart-data-table__td--center">{item.count}</td>
                  <td className="chart-data-table__td chart-data-table__td--right chart-data-table__td--muted">
                    {pct}%
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="chart-data-table__total">
              <td className="chart-data-table__td">Total</td>
              <td className="chart-data-table__td chart-data-table__td--center">{total}</td>
              <td className="chart-data-table__td chart-data-table__td--right">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

StatusDonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
  title: PropTypes.string,
};
